import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Field, inputClass } from '@/features/requests/request-form';
import { useAdminTeams, useInviteUser } from '@/hooks/use-admin';
import { ApiValidationError } from '@/lib/api';

/**
 * « Créer un nouvel utilisateur » (ADR-014): name, address and teams. The
 * account is linked to Entra at its first SSO login, by this address; an
 * invitation email leaves at once.
 */
export function UserInviteForm({ onDone }: { onDone: (result: { email: string; emailSent: boolean }) => void }) {
  const teams = useAdminTeams();
  const invite = useInviteUser();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const errors = invite.error instanceof ApiValidationError ? Object.fromEntries(invite.error.issues.map((i) => [String(i.path[0]), i.message])) : {};
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        invite.mutate({ firstName, lastName, email, teamIds }, { onSuccess: (r) => onDone({ email: email.trim().toLowerCase(), emailSent: r.emailSent }) });
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field id="invite-prenom" label="Prénom" required error={errors.firstName}>
          <input id="invite-prenom" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inputClass} autoComplete="off" />
        </Field>
        <Field id="invite-nom" label="Nom" required error={errors.lastName}>
          <input id="invite-nom" value={lastName} onChange={(e) => setLastName(e.target.value)} className={inputClass} autoComplete="off" />
        </Field>
      </div>
      <Field id="invite-email" label="Adresse email" required error={errors.email} hint="L'adresse de son compte Europa Group : elle relie le compte à sa première connexion.">
        <input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} autoComplete="off" />
      </Field>
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-2 p-0 text-sm font-bold">Équipes</legend>
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          {(teams.data ?? []).map((t) => (
            <label key={t.id} className="flex items-center gap-2.5 text-[13px]">
              <input type="checkbox" checked={teamIds.includes(t.id)} onChange={(e) => setTeamIds(e.target.checked ? [...teamIds, t.id] : teamIds.filter((id) => id !== t.id))} />
              {t.name}
            </label>
          ))}
        </div>
        {errors.teamIds && <span className="text-xs text-destructive">{errors.teamIds}</span>}
      </fieldset>
      <p className="m-0 text-xs text-muted-foreground">Un email d'invitation lui est envoyé. Son rôle (Référent, PO) vient d'Entra ID, pas d'ici.</p>
      {invite.error && !(invite.error instanceof ApiValidationError) && (
        <p role="alert" className="m-0 text-sm font-bold text-destructive">
          {invite.error.message}
        </p>
      )}
      <div className="flex justify-end gap-2.5 border-t border-border pt-4">
        <Button type="submit" disabled={invite.isPending || !firstName.trim() || !lastName.trim() || !email.trim() || teamIds.length === 0}>
          Créer et inviter
        </Button>
      </div>
    </form>
  );
}
